import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api, type Membership, type LoginResult, type AuthenticatedResult } from '@/lib/api'
import { type PlanId } from '@/lib/pricing'
import { useSubscriptionStore } from '@/lib/subscription-store'

export interface UserPermissions {
  canCreatePackages?: boolean
  canEditOrgPackages?: boolean
  canCreateOwnPackages?: boolean
  canPublishToOrg?: boolean
  canViewPricing?: boolean
  canEditPricing?: boolean
  canApplyDiscount?: boolean
  canViewOrgReports?: boolean
  canViewOwnReports?: boolean
  canExportData?: boolean
  canManageClients?: boolean
  canManageOwnClients?: boolean
  canManagePolicies?: boolean
  canManageTemplates?: boolean
  canCreateProposals?: boolean
  canCreateOnBehalf?: boolean
  canManageBilling?: boolean
  canManageTeam?: boolean
  canViewAuditLog?: boolean
  canManageSecurity?: boolean
  canViewAllProposals?: boolean
  canManageAllProposals?: boolean
  canManageOwnProposals?: boolean
  canViewAllClients?: boolean
  canManageCustomerPayments?: boolean
  canIssueRefunds?: boolean
  canWaiveCharges?: boolean
  canManageSubscriptionBilling?: boolean
  canManageSeats?: boolean
  canManageRoles?: boolean
}

interface User {
  id: string
  email: string
  name: string
  tenantId: string
  role: string
  avatarUrl?: string
  locale?: string
  permissions?: UserPermissions
  seatStatus?: string
}

interface AuthContextType {
  user: User | null
  plan: PlanId
  isAuthenticated: boolean
  isLoading: boolean
  memberships: Membership[]
  login: (email: string, password: string) => Promise<LoginResult>
  finishTwoFactor: (result: AuthenticatedResult) => void
  register: (data: {
    email: string
    password: string
    name: string
    businessName: string
    locale?: string
    currency?: string
    inviteCode?: string
  }) => Promise<void>
  logout: () => void
  updatePlan: (newPlan: PlanId) => void
  switchTenant: (tenantId: string) => Promise<LoginResult>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [plan, setPlan] = useState<PlanId>('free')
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [memberships, setMemberships] = useState<Membership[]>([])

  const refreshMemberships = async () => {
    try {
      const result = await api.getMemberships()
      setMemberships(result)
    } catch (error) {
      console.warn('Failed to load memberships:', error)
      setMemberships([])
    }
  }

  useEffect(() => {
    // Load auth state from persisted auth token/session
    const loadAuth = async () => {
      try {
        const userData = localStorage.getItem('user_data')

        // Try to fetch current user to validate active session
        try {
          const response = await api.validateToken()

          if (userData) {
            setUser(JSON.parse(userData))
          } else {
            setUser({
              id: response.id,
              email: response.email,
              name: response.name,
              tenantId: response.tenantId,
              role: response.role,
              avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${response.email}`,
            })
          }

          setIsAuthenticated(true)

          if (response.planId) {
            setPlan(response.planId as PlanId)
          } else {
            console.warn('No planId in validateToken response:', response)
            setPlan('free')
          }

          void refreshMemberships()
        } catch (error) {
          console.warn('Session validation failed:', error)
          api.clearAuthToken()
          localStorage.removeItem('user_data')
          setUser(null)
          setIsAuthenticated(false)
          setPlan('free')
          setMemberships([])
          // A stale/expired session leaves no guarantee that whatever account eventually
          // authenticates next (e.g. someone else on a shared browser) matches whichever
          // tenant's plan is cached here from before.
          useSubscriptionStore.getState().reset()
        }
      } catch (error) {
        console.error('Failed to load auth state:', error)
        api.clearAuthToken()
        localStorage.removeItem('user_data')
        localStorage.removeItem('user-plan')
      } finally {
        setIsLoading(false)
      }
    }

    loadAuth()
  }, [])

  // Apply a fully-authenticated result (from password login, 2FA verification, or
  // forced-enrollment confirmation) to the auth context. This is the ONLY place
  // that flips isAuthenticated to true / persists user_data — nothing is granted
  // before the second factor is satisfied.
  const applyAuthenticated = (response: AuthenticatedResult) => {
    api.refreshCsrfToken()

    const nextUser: User = {
      id: response.user.id,
      email: response.user.email,
      name: response.user.name,
      tenantId: response.user.tenantId,
      role: response.user.role,
      avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${response.user.email}`,
      locale: response.user.locale,
    }

    // A previous session on this browser (closed without logging out, or a different
    // account entirely) may have left a different tenant's plan cached here. Since
    // useCurrentPlan() trusts any resolved subscription once authenticated, that stale
    // data must not carry over into this new session.
    useSubscriptionStore.getState().reset()

    setUser(nextUser)
    setIsAuthenticated(true)
    localStorage.setItem('user_data', JSON.stringify(nextUser))

    if (response.tenant?.planId) {
      setPlan(response.tenant.planId as PlanId)
    } else {
      setPlan('free')
    }

    void refreshMemberships()
  }

  const login = async (email: string, password: string): Promise<LoginResult> => {
    setIsLoading(true)
    try {
      const response = await api.login(email, password)

      if (response.status === 'authenticated') {
        applyAuthenticated(response)
      }
      // For 'two_factor_required' / 'two_factor_setup_required' the caller (LoginPage)
      // drives the second step; no session state is set here.
      return response
    } catch (error) {
      console.error('Login failed:', error)
      api.clearAuthToken()
      localStorage.removeItem('user_data')
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const finishTwoFactor = (result: AuthenticatedResult) => {
    applyAuthenticated(result)
  }

  const switchTenant = async (tenantId: string): Promise<LoginResult> => {
    const response = await api.switchTenant(tenantId)

    // Target tenant may require a 2FA step-up or forced enrollment before the
    // switch completes — surface that to the caller and do not touch session state.
    if (response.status !== 'authenticated') {
      return response
    }

    const nextUser: User = {
      id: response.user.id,
      email: response.user.email,
      name: response.user.name,
      tenantId: response.user.tenantId,
      role: response.user.role,
      avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${response.user.email}`,
    }

    setUser(nextUser)
    localStorage.setItem('user_data', JSON.stringify(nextUser))

    if (response.tenant?.planId) {
      setPlan(response.tenant.planId as PlanId)
    }

    // The subscription store's cached plan/entitlements belong to the previous tenant and
    // are keyed in localStorage without a tenant scope - without a reset, useCurrentPlan()
    // would keep trusting that stale data (it now prefers the store once it has any
    // resolved subscription) until a fetch happens to overwrite it.
    useSubscriptionStore.getState().reset()

    await refreshMemberships()
    return response
  }

  const logout = () => {
    // Fire the logout request while the bearer token is still attached, so the
    // server can revoke it — clearing the token first would send an
    // unauthenticated request and leave a copied token usable until it expires.
    void api.logout().catch((error) => {
      console.warn('Logout request failed:', error)
    }).finally(() => {
      api.clearAuthToken()
    })

    setUser(null)
    setIsAuthenticated(false)
    setPlan('free')
    setMemberships([])
    localStorage.removeItem('user_data')
    // Clear the cached plan/entitlements so a different account logging in on the same
    // browser never briefly sees the previous account's persisted subscription data.
    useSubscriptionStore.getState().reset()
  }

  const register = async (data: {
    email: string
    password: string
    name: string
    businessName: string
    locale?: string
    currency?: string
    inviteCode?: string
  }) => {
    setIsLoading(true)
    try {
      await api.register(data)
      setUser(null)
      setIsAuthenticated(false)
      setPlan('free')
      localStorage.removeItem('user_data')
    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updatePlan = (newPlan: PlanId) => {
    setPlan(newPlan)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        plan,
        isAuthenticated,
        isLoading,
        memberships,
        login,
        finishTwoFactor,
        register,
        logout,
        updatePlan,
        switchTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
